/**
 * Client for the progress API (§9.3): `POST /api/progress/completions`, the
 * player's write path, and `GET /api/progress`, the read path that lets a
 * learner's completions follow them onto a second device.
 *
 * Deliberately thin: no retry, no backoff, no queue. The server buffers every
 * accepted row (server/sheets/writer.ts), completions are idempotent on
 * `clientEventId`, and the player keeps its own localStorage record. A second
 * queue on this side would only give the two a way to disagree.
 *
 * Follows `src/diagnostics/sheets-status.ts`: a relative URL (vite proxies
 * `/api` in dev, the same Cloud Run container serves both in prod) and an
 * injectable `fetchImpl` so it is testable without a network or MSW.
 *
 * NEITHER FUNCTION EVER THROWS. Finishing a lesson must not be able to raise an
 * exception into the player UI, and neither must booting the app — an
 * infrastructure problem the frontliner cannot see is not their problem to
 * handle. Both return a discriminated result for every outcome instead.
 */

import type {
  Completion,
  CompletionsRequest,
  CompletionsResponse,
  ProgressResponse,
  SessionIdentity,
} from '@shared/api-types'

const COMPLETIONS_URL = '/api/progress/completions'
const PROGRESS_URL = '/api/progress'

/** Lowest status the server uses for "my fault" — below it, the request is at fault. */
const SERVER_ERROR_STATUS = 500

/**
 * The `events` cap in `CompletionsRequestSchema` (server/routes/progress.ts).
 * A 21-event batch is a 400, so callers chunk on this rather than discover it.
 */
export const MAX_COMPLETION_EVENTS_PER_BATCH = 20

/** Failure shapes both paths share, so a caller can branch on `retryable` alone. */

/**
 * The request is wrong and will stay wrong (4xx, or rejected here before it was
 * sent). Terminal — `httpStatus` is absent when nothing was sent.
 */
interface RejectedResult {
  readonly status: 'rejected'
  readonly retryable: false
  readonly httpStatus?: number
  readonly detail: string
}

/** Server-side failure (5xx, incl. the 503 the read path uses). Retry later. */
interface UnavailableResult {
  readonly status: 'unavailable'
  readonly retryable: true
  readonly httpStatus: number
  readonly retryAfterMs?: number
  readonly detail: string
}

/** Never reached the API: transport error, or something that was not the API answered. */
interface UnreachableResult {
  readonly status: 'unreachable'
  readonly retryable: true
  readonly detail: string
}

/**
 * Outcome of one post. Branch on `status`; every failure carries `retryable`,
 * so a caller that only cares whether to keep the batch can check that alone.
 */
export type CompletionsResult =
  /** Server took the batch (202/200). Buffered — not yet in the Sheet. */
  | { readonly status: 'accepted'; readonly accepted: CompletionsResponse['accepted'] }
  | RejectedResult
  | UnavailableResult
  | UnreachableResult

/**
 * Outcome of one read. Same four-way shape as `CompletionsResult`, so the two
 * halves of this module stay legible together; `ok` carries the whole
 * `ProgressResponse` because `snapshot.source` ('live' | 'stale') tells a
 * caller how much to trust what it just got.
 */
export type ProgressResult =
  | { readonly status: 'ok'; readonly progress: ProgressResponse }
  | RejectedResult
  | UnavailableResult
  | UnreachableResult

/**
 * Post a completion batch. Resolves to a result for every outcome, including
 * network failure and a non-JSON body; it has no rejection path.
 */
export async function postCompletions(
  body: CompletionsRequest,
  fetchImpl: typeof fetch = fetch,
): Promise<CompletionsResult> {
  const sizeProblem = batchSizeProblem(body.events.length)
  if (sizeProblem) {
    // Terminal without a network call: the server would only answer 400.
    return { status: 'rejected', retryable: false, detail: sizeProblem }
  }

  let res: Response
  try {
    res = await fetchImpl(COMPLETIONS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch (err) {
    return { status: 'unreachable', retryable: true, detail: transportDetail(COMPLETIONS_URL, err) }
  }

  // Parsed for failures too: the 503 body carries `retryAfterMs`, the 400 body
  // carries the error code, and both are worth reporting to the caller.
  const payload = await readJson(res)

  if (res.ok) {
    if (!payload) {
      return { status: 'unreachable', retryable: true, detail: nonJsonDetail(res, COMPLETIONS_URL) }
    }
    return { status: 'accepted', accepted: acceptedIdsOf(payload) }
  }

  return failureResultFor(res, payload, COMPLETIONS_URL)
}

/**
 * Read one learner's completions back out of the system of record.
 *
 * The query parameters are lowercase `employee` / `role` and NOT the camelCase
 * `employeeId` / `roleId` the POST body uses. That asymmetry is real —
 * `server/routes/progress.ts:47-49` reads `c.req.query('employee')` while
 * `:26-38` validates `employeeId` — so it is mirrored here rather than
 * "corrected" into a 400 (`missing_employee`).
 *
 * Resolves for every outcome, exactly like `postCompletions`: a caller running
 * at boot cannot afford an exception, and `roleId: ''` is a legitimate identity
 * (src/session/identity.ts) that the route already defaults for.
 */
export async function fetchProgress(
  identity: SessionIdentity,
  fetchImpl: typeof fetch = fetch,
): Promise<ProgressResult> {
  const url = progressUrlFor(identity)

  let res: Response
  try {
    res = await fetchImpl(url)
  } catch (err) {
    return { status: 'unreachable', retryable: true, detail: transportDetail(PROGRESS_URL, err) }
  }

  const payload = await readJson(res)

  if (res.ok) {
    if (!payload) {
      return { status: 'unreachable', retryable: true, detail: nonJsonDetail(res, PROGRESS_URL) }
    }
    return { status: 'ok', progress: progressOf(payload) }
  }

  return failureResultFor(res, payload, PROGRESS_URL)
}

/**
 * Build `/api/progress?employee=…&role=…`.
 *
 * `URLSearchParams` rather than hand-rolled `encodeURIComponent`, because it is
 * the exact encoder Hono's query parser inverts: `_decodeURI`
 * (node_modules/hono/dist/utils/url.js:132) turns `+` back into a space before
 * decoding, so a `+` inside an employee id must arrive as `%2B`.
 * `encodeURIComponent` leaves a literal `+` untouched and the server would read
 * it as a space — silently querying a different employee. `&`, spaces and
 * non-ASCII are escaped either way; only `+` separates the two.
 */
function progressUrlFor(identity: SessionIdentity): string {
  const query = new URLSearchParams({ employee: identity.employeeId, role: identity.roleId })
  return `${PROGRESS_URL}?${query.toString()}`
}

/** The 4xx/5xx split, shared so both paths classify a failure identically. */
function failureResultFor(
  res: Response,
  payload: Record<string, unknown> | null,
  url: string,
): RejectedResult | UnavailableResult {
  if (res.status >= SERVER_ERROR_STATUS) {
    return {
      status: 'unavailable',
      retryable: true,
      httpStatus: res.status,
      retryAfterMs: retryAfterMsOf(payload),
      detail: describeFailure(res, payload, url),
    }
  }

  return {
    status: 'rejected',
    retryable: false,
    httpStatus: res.status,
    detail: describeFailure(res, payload, url),
  }
}

/** Reasons the server is guaranteed to reject the batch, or `null` if it is postable. */
function batchSizeProblem(eventCount: number): string | null {
  if (eventCount > MAX_COMPLETION_EVENTS_PER_BATCH) {
    return `batch of ${eventCount} events exceeds the server cap of ${MAX_COMPLETION_EVENTS_PER_BATCH} — split it`
  }
  if (eventCount === 0) return 'batch contains no events'
  return null
}

/** `null` when the body is not JSON — an HTML error page must not throw here. */
async function readJson(res: Response): Promise<Record<string, unknown> | null> {
  try {
    return (await res.json()) as Record<string, unknown> | null
  } catch {
    return null
  }
}

function acceptedIdsOf(payload: Record<string, unknown>): CompletionsResponse['accepted'] {
  const { accepted } = payload
  return Array.isArray(accepted) ? (accepted as readonly string[]) : []
}

/**
 * Same trust model as `acceptedIdsOf`: the contract is taken at its word for
 * the scalars, but `completions` is guaranteed to be an array before it leaves
 * here. `hydrateStages` iterates it, and a proxy — or a future server change —
 * must not be able to turn a boot-time read into a TypeError.
 *
 * The ELEMENTS are handed on by reference and never rebuilt, so every field the
 * server sent survives — `stage` and `requiresUnguided` included. Picking named
 * fields off here would silently drop whatever the row schema adds next.
 */
function progressOf(payload: Record<string, unknown>): ProgressResponse {
  const { completions } = payload
  return {
    ...(payload as unknown as ProgressResponse),
    completions: Array.isArray(completions) ? (completions as readonly Completion[]) : [],
  }
}

function retryAfterMsOf(payload: Record<string, unknown> | null): number | undefined {
  const value = payload?.retryAfterMs
  return typeof value === 'number' ? value : undefined
}

function describeFailure(
  res: Response,
  payload: Record<string, unknown> | null,
  url: string,
): string {
  const code = typeof payload?.error === 'string' ? ` ${payload.error}` : ''
  return `HTTP ${res.status}${code} from ${url}`
}

const transportDetail = (url: string, err: unknown): string =>
  `could not reach ${url} — ${messageOf(err)}`

const nonJsonDetail = (res: Response, url: string): string =>
  `HTTP ${res.status} from ${url} was not JSON — the SPA fallback or a proxy answered, not the API`

const messageOf = (err: unknown): string => (err instanceof Error ? err.message : String(err))
